import React, { Component } from 'react';
import {
 BrowserRouter
} from "react-router-dom";
// import { bindActionCreators } from 'redux';
import { Provider } from 'react-redux'
import { createStore, applyMiddleware, compose } from 'redux';
import { connect } from 'react-redux';

import moment from 'moment';
import thunk from 'redux-thunk';

import appReducer from './reducers';
import {
  sanitizeActiveDataLayers,
  sanitizeDataLayerOrder,
  sanitizeOverlayLayers,
  DISPLAYMODE_VERHUURDATA_HB,
  DISPLAYMODE_VERHUURDATA_VOERTUIGEN,
  DISPLAYMODE_RENTALS
} from './reducers/layers';
import App from './App';

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const md5 = require('md5');

// Get persistentState from localStorage
const theState = localStorage.getItem('CROWDD_reduxState')
let persistedState = {};
if (theState) {
  try {
    persistedState = JSON.parse(theState);
  } catch (e) {
    console.warn('Invalid persisted Redux state in localStorage, starting fresh:', e);
    localStorage.removeItem('CROWDD_reduxState');
  }
}

// Validate and clean persisted state to prevent reload loops
const validatePersistedState = (state) => {
  if (!state || !state.authentication || !state.authentication.user_data) {
    return {};
  }

  // Check if token exists and is not expired
  const userData = state.authentication.user_data;
  if (!userData.token) {
    // Clear authentication if no token
    return {
      ...state,
      authentication: { user_data: null }
    };
  }

  // Optional: Add token expiration check if your API provides expiration info
  // if (userData.token_expires && new Date(userData.token_expires) < new Date()) {
  //   return {
  //     ...state,
  //     authentication: { user_data: null }
  //   };
  // }

  return state;
};

persistedState = validatePersistedState(persistedState);

// HB is the standard rentals view. Apply that once to sessions that still
// have the previous default (individual rental vehicles), then leave later
// choices alone.
const RENTALS_DEFAULT_MIGRATION_KEY = 'CROWDD_rentals_default_hb_v1';
if (!localStorage.getItem(RENTALS_DEFAULT_MIGRATION_KEY)) {
  const layers = persistedState.layers;
  if (layers) {
    if (layers.view_rentals === DISPLAYMODE_VERHUURDATA_VOERTUIGEN) {
      layers.view_rentals = DISPLAYMODE_VERHUURDATA_HB;
    }
    const rentalsLayers = layers.active_data_layers?.[DISPLAYMODE_RENTALS];
    if (
      Array.isArray(rentalsLayers)
      && rentalsLayers.length === 1
      && rentalsLayers[0] === DISPLAYMODE_VERHUURDATA_VOERTUIGEN
    ) {
      layers.active_data_layers[DISPLAYMODE_RENTALS] = [DISPLAYMODE_VERHUURDATA_HB];
    }
  }
  localStorage.setItem(RENTALS_DEFAULT_MIGRATION_KEY, '1');
}

// The data-layer UI uses radio-button behaviour (one layer per display mode).
// Older persisted states may contain multiple active layers; sanitize them.
if (persistedState.layers && persistedState.layers.active_data_layers) {
  persistedState.layers.active_data_layers = sanitizeActiveDataLayers(
    persistedState.layers.active_data_layers
  );
}

if (persistedState.layers) {
  persistedState.layers.data_layer_order = sanitizeDataLayerOrder(
    persistedState.layers.data_layer_order
  );
  persistedState.layers.overlay_layers = sanitizeOverlayLayers(
    persistedState.layers.overlay_layers
  );
}

// Reset a stale persisted 'datum' filter to now *before* the store is created.
// App.tsx used to do this in an effect ~500ms after load, but by then the
// initial vehicles fetch is already in flight and the datum change would abort
// it and fetch everything again (doubling the load time for returning
// visitors). Shared links (?view=...) keep their persisted datum.
if (persistedState.filter && persistedState.filter.datum) {
  const isSharedLink = new URLSearchParams(window.location.search).get('view');
  const datumAgeMs = Date.now() - new Date(persistedState.filter.datum).getTime();
  const isMoreThan10MinutesAgo = isNaN(datumAgeMs) || datumAgeMs > 10 * 60 * 1000;
  if (!isSharedLink && isMoreThan10MinutesAgo) {
    persistedState.filter.datum = new Date().toISOString();
  }
}

window.process = { ...window.process }

export const store = createStore(
  appReducer,
  persistedState,
  composeEnhancers(applyMiddleware(thunk)),
);

// Store Redux state into localStorage
store.subscribe(() => {
  const storeState = store.getState();

  const oldFilterHash = localStorage.getItem('CROWDD_filterhash') || false;
  const newFilterHash = md5(JSON.stringify(storeState.filter))
  if(newFilterHash!==oldFilterHash) {
    // console.log("filter changed - invalidate data and zones here!");
    localStorage.setItem('CROWDD_filterhash', newFilterHash)
    
    // store.dispatch({ type: 'CLEAR_ZONES', payload: null});
    store.dispatch({ type: 'CLEAR_VEHICLES', payload: null});
    store.dispatch({ type: 'CLEAR_RENTALS_ORIGINS', payload: null});
    store.dispatch({ type: 'CLEAR_RENTALS_DESTINATIONS', payload: null});
  }
  
  // Debug: Track authentication state changes
  if (process.env.NODE_ENV === 'development') {
    const { debugReload } = require('./helpers/debug.js');
    debugReload.trackAuthState(storeState.authentication);
  }
  
  const storeStateToSaveInLocalStorage = {
    created: storeState.created ? storeState.created : moment().unix(),
    last_update: moment().unix(),
    authentication: storeState.authentication,
    filter: storeState.filter,
    layers: storeState.layers,
    ui: storeState.ui,
    policy_hubs: storeState.policy_hubs,
    service_areas: storeState.service_areas
  }
  localStorage.setItem('CROWDD_reduxState', JSON.stringify(storeStateToSaveInLocalStorage))
})

class AppProvider extends Component {
  render() {
    return (
      <Provider store={store}>
        <BrowserRouter>
          <App content={this.props.content} />
        </BrowserRouter>
      </Provider>
    )
  }
}

function connectWithStore(store, WrappedComponent, ...args) {
  let ConnectedWrappedComponent = connect(...args)(WrappedComponent)
  return function (props) {
    return <ConnectedWrappedComponent {...props} store={store} />
  }
}

function mapStateToProps(state) {
  return {
    // config: getConfig(state),
  }
}

function mapDispatchToProps(dispatch) {
  return {
    // actions: bindActionCreators(actions, dispatch),
  }
}

export default AppProvider = connectWithStore(
  store,
  AppProvider,
  mapStateToProps,
  mapDispatchToProps
)
